import { parsePasswordExpiry } from "../utils/passwordRules";
import { getLocalPasswordChangedAt } from "./loginPrefs";

export const SESSION_CHANGED_EVENT = "procurement-session-changed";

const notifySessionChanged = () => {
  window.dispatchEvent(new Event(SESSION_CHANGED_EVENT));
};
const HAS_SESSION_KEY = "hasSession";
const USERNAME_KEY = "username";
const ROLE_KEY = "role";
const IMAGE_PATH_KEY = "imagePath";
const COMPANY_CODES_KEY = "companyCodes";
const STRONG_PASSWORD_KEY = "isStrongPassword";
const PASSWORD_EXPIRED_KEY = "passwordExpired";
const PASSWORD_CHANGE_REASON_KEY = "passwordChangeReason";
const PASSWORD_EXPIRES_AT_KEY = "passwordExpiresAt";
const PASSWORD_DAYS_KEY = "passwordDaysRemaining";
const COMPANY_KEY = "company";

const LEGACY_LOCAL_KEYS = [
  "authToken",
  "refreshToken",
  "username",
  "role",
  "imagePath",
  "companyCodes",
  "isStrongPassword",
  "company",
];

let accessToken = null;
let pendingMfaToken = null;

const read = (key) => sessionStorage.getItem(key) || "";

export const hasSession = () => sessionStorage.getItem(HAS_SESSION_KEY) === "1";

// Other tabs only stop looking signed in. Company selection stays in this tab.
export const dropHasSession = () => {
  accessToken = null;
  sessionStorage.removeItem(HAS_SESSION_KEY);
};

const LOGOUT_BROADCAST_KEY = "procurement.logout";

export const getUsername = () => read(USERNAME_KEY);

export const getRole = () => read(ROLE_KEY);

export const isSuperAdmin = () => getRole() === "Super_Admin";

export const getImagePath = () => read(IMAGE_PATH_KEY);

export const getCompanyCode = () => read(COMPANY_KEY);

export const getCompanyCodes = () => {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(COMPANY_CODES_KEY) || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const isAssignedCompany = (code) => {
  if (!code) return false;
  return getCompanyCodes().includes(code);
};

export const clearCompanyCode = () => {
  sessionStorage.removeItem(COMPANY_KEY);
};

export const isStrongPassword = () =>
  sessionStorage.getItem(STRONG_PASSWORD_KEY) === "true";

export const isPasswordExpired = () =>
  sessionStorage.getItem(PASSWORD_EXPIRED_KEY) === "true";

export const needsPasswordChange = () => {
  if (isPasswordExpired()) return true;
  if (sessionStorage.getItem(STRONG_PASSWORD_KEY) === "false") return true;
  const days = getPasswordDaysRemaining();
  return days != null && days <= 0;
};

export const getPasswordChangeReason = () => {
  const stored = sessionStorage.getItem(PASSWORD_CHANGE_REASON_KEY);
  if (stored) return stored;
  const days = getPasswordDaysRemaining();
  if (days != null && days <= 0) return "expired";
  if (sessionStorage.getItem(STRONG_PASSWORD_KEY) === "false") return "weak";
  return "";
};

export const getPasswordDaysRemaining = () => {
  const raw = sessionStorage.getItem(PASSWORD_DAYS_KEY);
  if (raw == null || raw === "") return null;
  const days = Number(raw);
  return Number.isNaN(days) ? null : days;
};

export const getPasswordExpiresAt = () =>
  sessionStorage.getItem(PASSWORD_EXPIRES_AT_KEY) || "";

export const clearPasswordChangeRequired = () => {
  [
    PASSWORD_EXPIRED_KEY,
    PASSWORD_CHANGE_REASON_KEY,
    PASSWORD_EXPIRES_AT_KEY,
    PASSWORD_DAYS_KEY,
  ].forEach((key) => sessionStorage.removeItem(key));
  sessionStorage.setItem(STRONG_PASSWORD_KEY, "true");
};

export const getAccessToken = () => accessToken;

export const setAccessToken = (token) => {
  accessToken = token || null;
};

export const getPendingMfaToken = () => pendingMfaToken;

export const setPendingMfaToken = (token) => {
  pendingMfaToken = token || null;
};

export const clearPendingMfaToken = () => {
  pendingMfaToken = null;
};

export const setCompanyCode = (code) => {
  if (code && isAssignedCompany(code)) {
    sessionStorage.setItem(COMPANY_KEY, code);
    document.title = code;
    return true;
  }
  clearCompanyCode();
  return false;
};

export const setCompanyCodes = (codes) => {
  sessionStorage.setItem(
    COMPANY_CODES_KEY,
    JSON.stringify(Array.isArray(codes) ? codes : [])
  );
};

export const addAssignedCompanyCode = (companyCode) => {
  if (!companyCode) return;
  const codes = getCompanyCodes();
  if (!codes.includes(companyCode)) {
    setCompanyCodes([...codes, companyCode]);
  }
};

const stripLegacyAuthStorage = () => {
  LEGACY_LOCAL_KEYS.forEach((key) => localStorage.removeItem(key));
  sessionStorage.removeItem("isLogged");
};

export const setSessionFromProfile = (profile = {}) => {
  const username = profile.username ?? profile.users?.username;
  const role = profile.role ?? profile.users?.role;
  const imagePath = profile.imagePath ?? profile.users?.imagePath;
  const companyCodes =
    profile.companyCodes ??
    profile.users?.companies?.map((company) => company.code);

  const before = [
    getUsername(),
    getRole(),
    getImagePath(),
    JSON.stringify(getCompanyCodes()),
    sessionStorage.getItem(STRONG_PASSWORD_KEY) || "",
    getCompanyCode(),
  ].join("|");

  if (username) sessionStorage.setItem(USERNAME_KEY, username);
  if (role) sessionStorage.setItem(ROLE_KEY, role);
  if (imagePath != null) sessionStorage.setItem(IMAGE_PATH_KEY, imagePath);
  if (companyCodes != null) {
    const list = Array.isArray(companyCodes)
      ? companyCodes
      : [...companyCodes];
    setCompanyCodes(list);
    const current = getCompanyCode();
    if (current && !list.includes(current)) {
      clearCompanyCode();
    }
  }
  if (profile.isStrongPassword != null) {
    sessionStorage.setItem(
      STRONG_PASSWORD_KEY,
      profile.isStrongPassword ? "true" : "false"
    );
  }
  applyPasswordPolicy(profile, profile.isStrongPassword);
  sessionStorage.setItem(HAS_SESSION_KEY, "1");
  stripLegacyAuthStorage();
  const after = [
    getUsername(),
    getRole(),
    getImagePath(),
    JSON.stringify(getCompanyCodes()),
    sessionStorage.getItem(STRONG_PASSWORD_KEY) || "",
    getCompanyCode(),
  ].join("|");
  if (before !== after) {
    notifySessionChanged();
  }
};

export const clearSession = () => {
  accessToken = null;
  pendingMfaToken = null;
  [
    HAS_SESSION_KEY,
    USERNAME_KEY,
    ROLE_KEY,
    IMAGE_PATH_KEY,
    COMPANY_CODES_KEY,
    STRONG_PASSWORD_KEY,
    PASSWORD_EXPIRED_KEY,
    PASSWORD_CHANGE_REASON_KEY,
    PASSWORD_EXPIRES_AT_KEY,
    PASSWORD_DAYS_KEY,
    COMPANY_KEY,
    "isLogged",
  ].forEach((key) => sessionStorage.removeItem(key));
  stripLegacyAuthStorage();
  notifySessionChanged();
};

const profileFromResponse = (data) => ({
  username: data.username || data.users?.username,
  role: data.role || data.users?.role,
  imagePath: data.imagePath || data.users?.imagePath,
  companyCodes:
    data.companyCodes ||
    data.users?.companies?.map((company) => company.code) ||
    [],
  passwordExpired: data.passwordExpired ?? data.users?.passwordExpired,
  passwordExpiresAt: data.passwordExpiresAt || data.users?.passwordExpiresAt,
  passwordExpiryDate: data.passwordExpiryDate || data.users?.passwordExpiryDate,
  passwordChangedAt: data.passwordChangedAt || data.users?.passwordChangedAt,
  lastPasswordChange: data.lastPasswordChange || data.users?.lastPasswordChange,
  passwordUpdatedAt: data.passwordUpdatedAt || data.users?.passwordUpdatedAt,
  daysUntilPasswordExpiry:
    data.daysUntilPasswordExpiry ?? data.users?.daysUntilPasswordExpiry,
  message: data.message,
});

const applyPasswordPolicy = (profile = {}, strongPassword) => {
  const parsed = parsePasswordExpiry(profile);
  const fromLocal = parsePasswordExpiry({
    ...profile,
    passwordChangedAt:
      profile.passwordChangedAt ||
      profile.lastPasswordChange ||
      profile.passwordUpdatedAt ||
      getLocalPasswordChangedAt(),
  });
  if (parsed.expired) {
    sessionStorage.setItem(PASSWORD_EXPIRED_KEY, "true");
    sessionStorage.setItem(PASSWORD_CHANGE_REASON_KEY, "expired");
  } else {
    sessionStorage.removeItem(PASSWORD_EXPIRED_KEY);
    if (strongPassword === false) {
      sessionStorage.setItem(PASSWORD_CHANGE_REASON_KEY, "weak");
    } else if (strongPassword === true) {
      sessionStorage.removeItem(PASSWORD_CHANGE_REASON_KEY);
    }
  }

  const expiresAt = parsed.expiresAt || fromLocal.expiresAt;
  if (expiresAt) {
    sessionStorage.setItem(PASSWORD_EXPIRES_AT_KEY, expiresAt.toISOString());
  }
  const days =
    parsed.daysRemaining != null ? parsed.daysRemaining : fromLocal.daysRemaining;
  if (parsed.expired) {
    sessionStorage.setItem(PASSWORD_DAYS_KEY, "0");
  } else if (days != null && days > 0) {
    sessionStorage.setItem(PASSWORD_DAYS_KEY, String(days));
  }
};

export const applyProfileResponse = (data) => {
  if (!data) return false;
  const profile = profileFromResponse(data);
  if (!profile.username || !profile.role) return false;
  setSessionFromProfile(profile);
  return true;
};

let restoreSessionPromise = null;
let refreshInFlight = null;

const REFRESH_LOCK = "procurement-refresh";

export const SESSION_OK = "ok";
export const SESSION_UNAUTHORIZED = "unauthorized";
export const SESSION_UNAVAILABLE = "unavailable";

const runRefresh = async () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  let response;
  try {
    response = await fetch(`${apiUrl}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({}),
    });
  } catch {
    return { status: SESSION_UNAVAILABLE };
  }

  if (response.status === 401) {
    return { status: SESSION_UNAUTHORIZED };
  }
  if (!response.ok) {
    return { status: SESSION_UNAVAILABLE };
  }

  const data = await response.json().catch(() => ({}));
  if (!data.accessToken) {
    return { status: SESSION_UNAUTHORIZED };
  }
  setAccessToken(data.accessToken);
  return { status: SESSION_OK, tokens: data };
};

// One refresh at a time in this browser. Other tabs wait and then send the new cookie.
export const refreshAccessToken = () => {
  if (refreshInFlight) return refreshInFlight;

  const start = () => {
    if (navigator.locks?.request) {
      return navigator.locks.request(REFRESH_LOCK, runRefresh);
    }
    return runRefresh();
  };

  refreshInFlight = start().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
};

export const restoreSession = () => {
  if (restoreSessionPromise) return restoreSessionPromise;

  const apiUrl = process.env.REACT_APP_API_URL;
  restoreSessionPromise = (async () => {
    try {
      if (!getAccessToken()) {
        const refreshed = await refreshAccessToken();
        if (refreshed.status === SESSION_UNAUTHORIZED) {
          clearSession();
          return SESSION_UNAUTHORIZED;
        }
        if (refreshed.status !== SESSION_OK) {
          return SESSION_UNAVAILABLE;
        }
      }

      const response = await fetch(`${apiUrl}/api/get-profile`, {
        method: "GET",
        credentials: "include",
      });
      if (response.status === 401) {
        clearSession();
        return SESSION_UNAUTHORIZED;
      }
      if (!response.ok) {
        return SESSION_UNAVAILABLE;
      }
      const data = await response.json();
      if (!applyProfileResponse(data)) {
        clearSession();
        return SESSION_UNAUTHORIZED;
      }
      return SESSION_OK;
    } catch {
      if (!hasSession() && !getAccessToken()) {
        return SESSION_UNAUTHORIZED;
      }
      return SESSION_UNAVAILABLE;
    } finally {
      restoreSessionPromise = null;
    }
  })();

  return restoreSessionPromise;
};

export const logoutSession = async () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  try {
    localStorage.setItem(LOGOUT_BROADCAST_KEY, Date.now().toString());
  } catch {
    // still sign out this tab
  }
  try {
    await fetch(`${apiUrl}/logout`, { method: "POST", credentials: "include" });
  } catch {
    // still clear the local session
  }
  clearSession();
};

// Local sign-out only. The Logout button and idle timeout call logoutSession,
// which is what revokes the server session.
export const redirectToLogin = () => {
  clearSession();
  if (window.location.pathname !== "/login") {
    window.location.href = "/login";
  }
};
